/**
 * Authenticated shell — top nav from /api/webui/manifest + page outlet.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { SessionUser } from '../types.js';
import { logout } from '../auth.js';
import { ChatPage } from './Chat.js';
import { AdminPage } from './Admin.js';
import { BillingPage } from './Billing.js';
import { NotificationsPage } from './NotificationsPage.js';
import { TasksPage } from './TasksPage.js';
import {
  Bell,
  CreditCard,
  LayoutDashboard,
  List,
  Loader2,
  LogOut,
  MessageSquare,
  NotebookPen,
  Shield,
} from 'lucide-react';
import { NotesPage } from './NotesPage.js';
import {
  WidgetRegistryContext,
  buildClientRegistry,
  type WidgetKindRegistration,
} from '../widgets/registry-context.js';
import { ProductTourProvider } from '../components/product-tour/ProductTourContext.js';
import { ProductTourHost } from '../components/product-tour/ProductTourHost.js';

export interface ManifestNavItem {
  id: string;
  label: string;
  path: string;
  icon?: string;
  order?: number;
  adminOnly?: boolean;
  badgePath?: string;
  framework?: boolean;
}

export interface ManifestRoute {
  path: string;
  pageKind: 'notifications' | 'tasks' | 'notes' | 'iframe';
  apiBase?: string;
  iframeSrc?: string;
  title?: string;
}

export interface ChatEmptyStateManifest {
  title: string;
  body: string[];
  bullets?: string[];
  starters?: Array<{ label: string; message: string }>;
  footer?: string;
}

export interface WebUiManifest {
  agentKey: string | null;
  productName: string;
  defaultPath: string;
  nav: ManifestNavItem[];
  routes: ManifestRoute[];
  widgets?: WidgetKindRegistration[];
  chatEmptyState?: ChatEmptyStateManifest | null;
  /** Billing entry lives in chat sidebar user footer, not top nav. */
  billingEnabled?: boolean;
}

interface ShellProps {
  session: SessionUser;
  path: string;
  navigate: (path: string) => void;
}

function iconFor(name?: string) {
  switch (name) {
    case 'bell':
      return Bell;
    case 'layout-dashboard':
      return LayoutDashboard;
    case 'list':
      return List;
    case 'notebook-pen':
      return NotebookPen;
    case 'shield':
      return Shield;
    case 'credit-card':
      return CreditCard;
    case 'message-square':
    default:
      return MessageSquare;
  }
}

function matchRoute(path: string, routes: ManifestRoute[]): ManifestRoute | null {
  for (const r of routes) {
    if (r.path === path) return r;
    // prefix match for /tasks/:id later
    if (path.startsWith(r.path + '/')) return r;
  }
  return null;
}

export function Shell({ session, path, navigate }: ShellProps) {
  const [manifest, setManifest] = useState<WebUiManifest | null>(null);
  const [badges, setBadges] = useState<Record<string, number>>({});
  const [loadError, setLoadError] = useState<string | null>(null);
  const [chatBootReady, setChatBootReady] = useState(false);

  const [manifestFetchOk, setManifestFetchOk] = useState(false);

  const loadManifest = useCallback(async () => {
    try {
      const res = await fetch('/api/webui/manifest', { credentials: 'include' });
      if (res.status === 401) {
        // Cookie gone / session dead — force re-login (do not soft-fallback).
        const { clearStoredSession } = await import('../auth.js');
        clearStoredSession();
        window.location.assign('/login');
        return;
      }
      if (!res.ok) throw new Error(`manifest ${res.status}`);
      const data = (await res.json()) as WebUiManifest;
      setManifest(data);
      setManifestFetchOk(true);
      setLoadError(null);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : String(e));
      setManifestFetchOk(false);
      // Fallback: chat only (widgets unavailable) — only for non-auth failures
      setManifest({
        agentKey: null,
        productName: 'Agent',
        defaultPath: '/',
        nav: [
          { id: 'chat', label: 'Chat', path: '/', icon: 'message-square', framework: true },
        ],
        routes: [],
        widgets: undefined,
        chatEmptyState: null,
        billingEnabled: false,
      });
    }
  }, []);

  const widgetRegistry = useMemo(
    () =>
      buildClientRegistry({
        agentKey: manifest?.agentKey ?? null,
        widgets: manifest?.widgets,
        fetchOk: manifestFetchOk,
        fetchError: loadError ?? undefined,
      }),
    [manifest, manifestFetchOk, loadError],
  );

  useEffect(() => {
    void loadManifest();
  }, [loadManifest]);

  // Badge polling
  useEffect(() => {
    if (!manifest) return;
    let cancelled = false;
    const poll = async () => {
      const next: Record<string, number> = {};
      await Promise.all(
        manifest.nav
          .filter((n) => n.badgePath)
          .map(async (n) => {
            try {
              const res = await fetch(n.badgePath!, { credentials: 'include' });
              if (!res.ok) return;
              const data = (await res.json()) as { count?: number };
              if (typeof data.count === 'number') next[n.id] = data.count;
            } catch {
              /* ignore badge errors */
            }
          }),
      );
      if (!cancelled) setBadges(next);
    };
    void poll();
    const t = setInterval(() => void poll(), 30_000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [manifest]);

  // After login (session flag), land on domain defaultPath once if configured.
  useEffect(() => {
    if (!manifest?.defaultPath || manifest.defaultPath === '/') return;
    const ok = manifest.nav.some((n) => n.path === manifest.defaultPath);
    if (!ok) return;
    try {
      if (sessionStorage.getItem('utarus_default_landed') === '1') return;
      // Only auto-land if we are on chat home
      if (path !== '/' && path !== '') return;
      sessionStorage.setItem('utarus_default_landed', '1');
      navigate(manifest.defaultPath);
    } catch {
      /* private mode */
    }
  }, [manifest, path, navigate]);

  if (!manifest) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-stone-50">
        <Loader2 className="h-6 w-6 animate-spin text-stone-400" />
      </div>
    );
  }

  if (path.startsWith('/admin')) {
    if (session.type !== 'admin') {
      return (
        <div className="flex min-h-dvh items-center justify-center bg-stone-50 text-sm text-stone-600">
          Admins only.{' '}
          <button onClick={() => navigate('/')} className="ml-2 text-stone-900 underline hover:text-stone-600">
            Back to chat
          </button>
        </div>
      );
    }
    return <AdminPage session={session} onBack={() => navigate('/')} />;
  }

  // Billing is a modal over chat (sidebar open + Stripe/deep-link /billing*).
  const billingOpen = path === '/billing' || path.startsWith('/billing/');
  // Content under the modal uses chat home when on /billing.
  const contentPath = billingOpen ? '/' : path;

  // Framework-owned pages before domain matchRoute (K6)
  let route: ManifestRoute | null = null;
  if (contentPath === '/tasks' || contentPath.startsWith('/tasks/')) {
    route = { path: '/tasks', pageKind: 'tasks', apiBase: '/api/tasks', title: 'Tasks' };
  } else if (contentPath === '/notifications' || contentPath.startsWith('/notifications/')) {
    route = {
      path: '/notifications',
      pageKind: 'notifications',
      apiBase: '/api/notifications',
      title: 'Notifications',
    };
  } else if (contentPath === '/notes' || contentPath.startsWith('/notes/')) {
    route = { path: '/notes', pageKind: 'notes', apiBase: '/api/notes', title: 'Note' };
  } else {
    route = matchRoute(contentPath, manifest.routes);
  }
  const showChat = contentPath === '/' || contentPath === '';

  return (
    <ProductTourProvider>
      <div className="flex h-dvh flex-col overflow-hidden bg-stone-50">
        <header className="sticky top-0 z-20 shrink-0 border-b border-stone-200/80 bg-stone-50/90 backdrop-blur">
          <div className="flex h-12 items-center gap-3 px-3 sm:px-4">
            <div className="truncate font-serif text-sm font-semibold text-stone-900">
              {manifest.productName}
            </div>
            <nav className="flex flex-1 items-center gap-1 overflow-x-auto">
              {manifest.nav.map((item) => {
                const Icon = iconFor(item.icon);
                const active =
                  item.path === '/'
                    ? showChat
                    : contentPath === item.path || contentPath.startsWith(item.path + '/');
                const badge = badges[item.id] ?? 0;
                return (
                  <button
                    key={item.id}
                    type="button"
                    data-tour={`shell-nav-item-${item.id}`}
                    onClick={() => navigate(item.path)}
                    className={`relative inline-flex items-center gap-1.5 rounded-xl px-2.5 py-1.5 text-xs font-medium transition ${
                      active
                        ? 'bg-stone-900 text-stone-50'
                        : 'text-stone-600 hover:bg-stone-100 hover:text-stone-900'
                    }`}
                  >
                    <Icon className="h-3.5 w-3.5 shrink-0" />
                    <span>{item.label}</span>
                    {badge > 0 && (
                      <span
                        className={`ml-0.5 min-w-[1.1rem] rounded-full px-1 text-[10px] font-semibold leading-4 ${
                          active ? 'bg-stone-50 text-stone-900' : 'bg-coral text-white'
                        }`}
                      >
                        {badge > 99 ? '99+' : badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>
            <div className="hidden text-xs text-stone-500 sm:block">
              {session.displayName || session.slug}
            </div>
            <button
              type="button"
              onClick={() => void logout()}
              className="inline-flex items-center gap-1 rounded-xl px-2 py-1.5 text-xs text-stone-600 hover:bg-stone-100"
              title="Log out"
            >
              <LogOut className="h-3.5 w-3.5" />
            </button>
          </div>
          {loadError && (
            <div className="border-t border-amber-100 bg-amber-50 px-3 py-1 text-center text-[11px] text-amber-800">
              Manifest fallback: {loadError}
            </div>
          )}
        </header>

        <main className="flex min-h-0 flex-1 flex-col overflow-y-auto">
          <WidgetRegistryContext.Provider value={widgetRegistry}>
            {showChat && (
              <ChatPage
                session={session}
                emptyState={manifest.chatEmptyState ?? null}
                billingEnabled={Boolean(manifest.billingEnabled)}
                onOpenBilling={() => navigate('/billing')}
                onBootReadyChange={setChatBootReady}
              />
            )}
          </WidgetRegistryContext.Provider>
          {!showChat && route?.pageKind === 'notifications' && (
            <NotificationsPage
              apiBase={route.apiBase || ''}
              title={route.title || 'Notifications'}
              navigate={navigate}
              viewerSlug={session.slug}
              onChanged={() => {
                // force badge refresh
                setManifest((m) => (m ? { ...m } : m));
              }}
            />
          )}
          {!showChat && route?.pageKind === 'tasks' && (
            <TasksPage
              apiBase={route.apiBase || ''}
              title={route.title || 'Tasks'}
              navigate={navigate}
              viewerSlug={session.slug}
            />
          )}
          {!showChat && route?.pageKind === 'notes' && (
            <NotesPage
              apiBase={route.apiBase || '/api/notes'}
              title={route.title || 'Note'}
              navigate={navigate}
              path={contentPath}
              viewerSlug={session.slug}
            />
          )}
          {!showChat && route?.pageKind === 'iframe' && route.iframeSrc && (
            <iframe title={route.title || 'Domain'} src={route.iframeSrc} className="h-[calc(100dvh-3rem)] w-full border-0" />
          )}
          {!showChat && !route && (
            <div className="flex min-h-[50vh] flex-col items-center justify-center gap-2 text-sm text-stone-600">
              <p>Page not found.</p>
              <button type="button" className="text-stone-900 underline hover:text-stone-600" onClick={() => navigate('/')}>
                Back to chat
              </button>
            </div>
          )}
        </main>

        {billingOpen && (
          <BillingPage
            session={session}
            onClose={() => navigate('/')}
          />
        )}

        <ProductTourHost
          session={session}
          productName={manifest.productName}
          nav={manifest.nav}
          billingEnabled={Boolean(manifest.billingEnabled)}
          onChat={showChat}
          billingOpen={billingOpen}
          chatBootReady={showChat && chatBootReady}
        />
      </div>
    </ProductTourProvider>
  );
}
